import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  /** When false, the header X close button is hidden. Default true. */
  showCloseButton?: boolean;
}

const sizeClasses = {
  sm: "w-[calc(100vw-2rem)] max-w-md",
  md: "w-[calc(100vw-2rem)] max-w-lg",
  lg: "w-[calc(100vw-2rem)] max-w-2xl",
  xl: "w-[calc(100vw-2rem)] max-w-4xl sm:max-w-4xl",
  full: "w-[calc(100vw-2rem)] sm:w-[90vw] sm:max-w-[90vw]",
};

export const Dialog: React.FC<DialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  size = "lg",
  showCloseButton = true,
}) => {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [open, onOpenChange]);

  if (!open) return null;

  const content = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      {/* Backdrop - white with blur, covers full screen, receives click to close */}
      <div
        className="absolute inset-0 bg-white/80 backdrop-blur-sm cursor-pointer"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      {/* Dialog panel - above backdrop */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "dialog-title" : undefined}
        className={cn(
          "relative z-10 bg-card border border-input rounded-lg shadow-xl w-full overflow-auto max-h-[min(90vh,calc(100dvh-2rem))] my-auto",
          sizeClasses[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || description) && (
          <div className="flex items-start justify-between p-6 pb-0">
            <div>
              {title && (
                <h2 id="dialog-title" className="text-xl font-semibold">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="shrink-0 -mr-2 -mt-2"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
        <div className={cn("p-6", !title && !description && "pt-6")}>
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
